import React, { useState, useEffect, useContext, useCallback } from 'react';
import { 
  Calendar, 
  User, 
  Banknote, 
  Clock, 
  Plus, 
  Minus, 
  AlertCircle,
  Save,
  X,
  Search,
  ChevronRight,
  Info,
  Loader2
} from 'lucide-react';
import { WarehouseContext } from '../../Utility/UserContext';
import { 
  getUsers, 
  getPendingAdvance, 
  createSalaryPayment,
  getBankBalance
} from '../../Utility/Api';
import { toast } from 'react-toastify';
import { cn } from '../../lib/utils';

const SalaryForm = ({ onClose, onSuccess }) => {
  const { shopItem, subCompany, UserItem } = useContext(WarehouseContext);
  
  const [salaryType, setSalaryType] = useState('salary');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [pendingAdvance, setPendingAdvance] = useState(0);
  const [advanceDeduction, setAdvanceDeduction] = useState(0);
  const [isDeductingAdvance, setIsDeductingAdvance] = useState(false);
  const [bankBalance, setBankBalance] = useState(null);
  const [amountError, setAmountError] = useState("");
  
  const [formData, setFormData] = useState({
    amount: '',
    payment_mode: 'cash',
    remarks: '',
    from_date: '',
    to_date: '',
    payment_date: new Date().toISOString().split('T')[0],
    is_multi_ot: false
  });

  const [selectedDates, setSelectedDates] = useState([]);
  const [otEntries, setOtEntries] = useState([{ date: new Date().toISOString().split('T')[0], hours: '', amount: '' }]);

  const fetchBankBalance = useCallback(async () => {
    if (shopItem?.id) {
      try {
        const response = await getBankBalance(shopItem.id);
        if (response?.data?.bank_balance !== undefined) {
          setBankBalance(response.data.bank_balance);
        }
      } catch (error) {
        console.error("Error fetching bank balance:", error);
      }
    }
  }, [shopItem]);

  useEffect(() => {
    fetchBankBalance();
  }, [fetchBankBalance]);

  useEffect(() => {
    if (bankBalance !== null) {
      const enteredAmount = parseFloat(formData.amount || 0);
      const payback = isDeductingAdvance ? parseFloat(advanceDeduction || 0) : 0;
      const netPayable = enteredAmount - payback;
      
      if (netPayable > bankBalance) {
        setAmountError(`Exceeds bank balance! Available: ₹${bankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      } else {
        setAmountError("");
      }
    } else {
      setAmountError("");
    }
  }, [formData.amount, advanceDeduction, isDeductingAdvance, bankBalance]);

  // Fetch employees on mount or search
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await getUsers(1, searchQuery, shopItem?.id);
        if (response.data && response.data.data) {
          setEmployees(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };

    const delayDebounce = setTimeout(() => {
      if (shopItem?.id) fetchEmployees();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, shopItem]);

  // Fetch pending advance when employee changes
  useEffect(() => {
    if (selectedEmployee?.user_Id && shopItem?.id && subCompany?.id) {
      const fetchAdvance = async () => {
        try {
          const response = await getPendingAdvance({
            employee_id: selectedEmployee.user_Id,
            shop_id: subCompany.id,
            branch_id: shopItem.id
          });
          if (response.data && response.data.status === 'success') {
            setPendingAdvance(response.data.balance);
            // Reset deduction if balance is 0
            if (response.data.balance <= 0) {
              setAdvanceDeduction(0);
              setIsDeductingAdvance(false);
            }
          }
        } catch (error) {
          console.error('Error fetching advance:', error);
        }
      };
      fetchAdvance();
    } else {
      setPendingAdvance(0);
      setAdvanceDeduction(0);
      setIsDeductingAdvance(false);
    }
  }, [selectedEmployee, shopItem, subCompany]);

  // Generate dates when from/to changes
  useEffect(() => {
    if (salaryType === 'salary' && formData.from_date && formData.to_date) {
      const start = new Date(formData.from_date);
      const end = new Date(formData.to_date);
      const dates = [];
      let current = new Date(start);
      
      while (current <= end) {
        dates.push({
          date: current.toISOString().split('T')[0],
          is_working: true
        });
        current.setDate(current.getDate() + 1);
      }
      setSelectedDates(dates);
    }
  }, [formData.from_date, formData.to_date, salaryType]);

  const toggleDate = (index) => {
    const newDates = [...selectedDates];
    newDates[index].is_working = !newDates[index].is_working;
    setSelectedDates(newDates);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Date range validation
    if (name === 'from_date' && formData.to_date && value > formData.to_date) {
      toast.warning("From date cannot be later than To date");
      return;
    }
    if (name === 'to_date' && formData.from_date && value < formData.from_date) {
      toast.warning("To date cannot be earlier than From date");
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addOtRow = () => {
    setOtEntries([...otEntries, { date: new Date().toISOString().split('T')[0], hours: '', amount: '' }]);
  };

  const removeOtRow = (index) => {
    setOtEntries(otEntries.filter((_, i) => i !== index));
  };

  const updateOtRow = (index, field, value) => {
    const newEntries = [...otEntries];
    newEntries[index][field] = value;
    setOtEntries(newEntries);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedEmployee) return toast.error("Please select an employee");
    if (!formData.amount || formData.amount <= 0) return toast.error("Please enter a valid amount");
    
    const enteredAmount = parseFloat(formData.amount || 0);
    const payback = isDeductingAdvance ? parseFloat(advanceDeduction || 0) : 0;
    const netPayable = enteredAmount - payback;
    
    if (bankBalance !== null && netPayable > bankBalance) {
      toast.error(`Insufficient bank balance! Available: ₹${bankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, Required: ₹${netPayable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      return;
    }
    
    const payload = {
      employee: selectedEmployee.user_Id,
      shop_id: subCompany.id,
      branch_id: shopItem.id,
      type: salaryType,
      amount: formData.amount,
      payment_mode: formData.payment_mode,
      remarks: formData.remarks,
      payment_date: formData.payment_date,
      created_by: UserItem?.user_id || 'System',
      advance_deduction: isDeductingAdvance ? advanceDeduction : 0
    };

    if (salaryType === 'salary') {
      if (!formData.from_date || !formData.to_date) {
        return toast.error("Please select both From and To dates");
      }
      if (formData.from_date > formData.to_date) {
        return toast.error("From date cannot be later than To date");
      }

      payload.from_date = formData.from_date;
      payload.to_date = formData.to_date;
      payload.selected_dates = selectedDates;
      
      const workingDays = selectedDates.filter(d => d.is_working).length;
      if (workingDays === 0) return toast.error("Please select at least one working day");
    }

    if (salaryType === 'ot') {
      payload.ot_entries = otEntries;
    }

    setIsSaving(true);
    try {
      const response = await createSalaryPayment(payload);
      if (response.data.status === 'success') {
        toast.success("Payment recorded successfully");
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create payment");
    } finally {
      setIsSaving(false);
    }
  };

  const salaryTypes = [
    { id: 'salary', label: 'Salary', icon: Banknote, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'bonus', label: 'Bonus', icon: Plus, color: 'text-green-600', bg: 'bg-green-50' },
    { id: 'ot', label: 'OT', icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50' },
    { id: 'advance', label: 'Advance', icon: AlertCircle, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white">
            <Calculator className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">New Payment Entry</h2>
            <p className="text-xs text-slate-500 font-medium">{subCompany?.title} • {shopItem?.shop_title}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        {/* Salary Type Selector */}
        <div className="grid grid-cols-4 gap-4">
          {salaryTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSalaryType(type.id)}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all group",
                salaryType === type.id 
                  ? `border-blue-600 ${type.bg}` 
                  : "border-slate-100 hover:border-slate-200 bg-white"
              )}
            >
              <type.icon className={cn("w-6 h-6", salaryType === type.id ? type.color : "text-slate-400 group-hover:text-slate-500")} />
              <span className={cn("text-sm font-bold", salaryType === type.id ? "text-blue-700" : "text-slate-500")}>
                {type.label}
              </span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Core Data */}
          <div className="space-y-6">
            {/* Employee Search */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <User className="w-3 h-3" /> Select Employee
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsSearching(true);
                  }}
                  onFocus={() => setIsSearching(true)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm"
                />
                {isSearching && searchQuery && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2">
                    {employees.map((emp) => (
                      <button
                        key={emp.user_Id}
                        onClick={() => {
                          setSelectedEmployee(emp);
                          setSearchQuery(emp.full_name);
                          setIsSearching(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center justify-between group transition-colors"
                      >
                        <div>
                          <p className="font-bold text-slate-700 group-hover:text-blue-600">{emp.full_name}</p>
                          <p className="text-xs text-slate-400">{emp.designation} • ID: {emp.employee_id}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-400" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Advance Payback Section - Moved here as requested */}
              {salaryType === 'salary' && pendingAdvance > 0 && (
                <div className="mt-4 p-4 rounded-xl bg-orange-50 border border-orange-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                        <AlertCircle className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-orange-400 uppercase leading-none">Pending Advance</p>
                        <p className="text-sm font-black text-orange-700">₹{pendingAdvance}</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={isDeductingAdvance}
                        onChange={(e) => {
                          setIsDeductingAdvance(e.target.checked);
                          if (!e.target.checked) setAdvanceDeduction(0);
                        }}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      <span className="ml-3 text-xs font-bold text-slate-600">Payback</span>
                    </label>
                  </div>

                  {isDeductingAdvance && (
                    <div className="pt-2 border-t border-orange-100 flex items-center gap-4 animate-in zoom-in-95 duration-200">
                      <div className="flex-1 space-y-1.5">
                        <label className="text-[10px] font-bold text-orange-400 uppercase">Deduction Amount</label>
                        <div className="relative">
                          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-orange-400 font-bold">₹</span>
                          <input
                            type="number"
                            max={pendingAdvance}
                            value={advanceDeduction}
                            onChange={(e) => {
                              const val = Math.min(Number(e.target.value), pendingAdvance);
                              setAdvanceDeduction(val);
                            }}
                            className="block w-full pl-7 pr-3 py-2 border border-orange-200 rounded-lg bg-white text-sm font-bold text-orange-700 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="text-xs text-orange-400 italic mt-4 max-w-[120px]">
                        Max allowed: ₹{pendingAdvance}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Amount and Payment Mode */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Amount</label>
                  {bankBalance !== null && (
                    <span className={cn(
                      "text-[10px] font-bold px-2 py-0.5 rounded-full border",
                      bankBalance <= 0 
                        ? "bg-red-50 text-red-600 border-red-200" 
                        : "bg-emerald-50 text-emerald-600 border-emerald-200"
                    )}>
                      Avail: ₹{bankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold">₹</span>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className={cn(
                      "block w-full pl-7 pr-3 py-2.5 border rounded-lg font-bold transition-all text-sm",
                      amountError 
                        ? "border-red-300 bg-red-50 text-red-900 focus:ring-red-500/20 focus:border-red-500" 
                        : "border-slate-200 bg-slate-50 text-slate-800 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    )}
                    placeholder="0.00"
                  />
                </div>
                {amountError && (
                  <p className="text-[10px] font-bold text-red-600 mt-1 animate-in fade-in slide-in-from-top-1">{amountError}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Payment Mode</label>
                <select
                  name="payment_mode"
                  value={formData.payment_mode}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-sm font-medium"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI / Online</option>
                  <option value="card">Card</option>
                </select>
              </div>
            </div>


            {/* Remarks */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Remarks / Notes</label>
              <textarea
                name="remarks"
                rows="2"
                value={formData.remarks}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm"
                placeholder="Optional notes for this payment..."
              ></textarea>
            </div>
          </div>

          {/* Right Column: Dynamic Rendering based on Type */}
          <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 min-h-[300px]">
            {salaryType === 'salary' && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" /> Attendance Period
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">From Date</label>
                    <input
                      type="date"
                      name="from_date"
                      value={formData.from_date}
                      max={formData.to_date}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-xs"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">To Date</label>
                    <input
                      type="date"
                      name="to_date"
                      value={formData.to_date}
                      min={formData.from_date}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-xs"
                    />
                  </div>
                </div>

                {selectedDates.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">Select Working Days ({selectedDates.filter(d => d.is_working).length})</label>
                      <button 
                        type="button"
                        onClick={() => setSelectedDates(selectedDates.map(d => ({ ...d, is_working: true })))}
                        className="text-[10px] font-bold text-blue-600 hover:underline"
                      >
                        Select All
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {selectedDates.map((date, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => toggleDate(idx)}
                          className={cn(
                            "aspect-square rounded text-[10px] font-bold flex items-center justify-center transition-all",
                            date.is_working 
                              ? "bg-blue-600 text-white shadow-sm" 
                              : "bg-white border border-slate-200 text-slate-300 hover:border-slate-300"
                          )}
                          title={date.date}
                        >
                          {new Date(date.date).getDate()}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400 italic">* Click on dates to toggle attendance.</p>
                  </div>
                )}
              </div>
            )}

            {salaryType === 'ot' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-500" /> OT Breakdown
                  </h3>
                  <button 
                    type="button"
                    onClick={addOtRow}
                    className="p-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                  {otEntries.map((entry, idx) => (
                    <div key={idx} className="flex items-end gap-2 bg-white p-3 rounded-lg border border-slate-200 animate-in slide-in-from-right-4 duration-200">
                      <div className="flex-1 space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">Date</label>
                        <input
                          type="date"
                          value={entry.date}
                          onChange={(e) => updateOtRow(idx, 'date', e.target.value)}
                          className="block w-full px-2 py-1.5 border border-slate-100 rounded bg-slate-50 text-[10px]"
                        />
                      </div>
                      <div className="w-16 space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">Hours</label>
                        <input
                          type="number"
                          value={entry.hours}
                          onChange={(e) => updateOtRow(idx, 'hours', e.target.value)}
                          className="block w-full px-2 py-1.5 border border-slate-100 rounded bg-slate-50 text-[10px]"
                          placeholder="0"
                        />
                      </div>
                      <div className="w-20 space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">Amt</label>
                        <input
                          type="number"
                          value={entry.amount}
                          onChange={(e) => updateOtRow(idx, 'amount', e.target.value)}
                          className="block w-full px-2 py-1.5 border border-slate-100 rounded bg-slate-50 text-[10px]"
                          placeholder="0.00"
                        />
                      </div>
                      {otEntries.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => removeOtRow(idx)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(salaryType === 'bonus' || salaryType === 'advance') && (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                <div className={cn("w-16 h-16 rounded-full flex items-center justify-center", salaryType === 'bonus' ? "bg-green-100 text-green-600" : "bg-purple-100 text-purple-600")}>
                  {salaryType === 'bonus' ? <Plus className="w-8 h-8" /> : <AlertCircle className="w-8 h-8" />}
                </div>
                <div>
                  <h4 className="font-bold text-slate-800">{salaryType === 'bonus' ? 'Bonus Payment' : 'Advance Issuance'}</h4>
                  <p className="text-xs text-slate-500 max-w-[200px]">
                    {salaryType === 'bonus' 
                      ? 'No attendance tracking required for bonus payments.' 
                      : 'Advances are automatically added to the employee pending balance.'}
                  </p>
                </div>
                <div className="w-full space-y-1.5 text-left">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Payment Date</label>
                  <input
                    type="date"
                    name="payment_date"
                    value={formData.payment_date}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-xs"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer / Summary Card */}
      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="space-y-0.5">
            <p className="text-[10px] font-bold text-slate-400 uppercase">Gross Salary</p>
            <p className="text-lg font-black text-slate-900">₹{formData.amount || 0}</p>
          </div>
          {isDeductingAdvance && advanceDeduction > 0 && (
            <>
              <div className="text-slate-300 font-light text-2xl">-</div>
              <div className="space-y-0.5">
                <p className="text-[10px] font-bold text-orange-400 uppercase">Advance Payback</p>
                <p className="text-lg font-black text-orange-600">₹{advanceDeduction}</p>
              </div>
            </>
          )}
          <div className="text-slate-300 font-light text-2xl">=</div>
          <div className="space-y-0.5">
            <p className="text-[10px] font-bold text-blue-400 uppercase">Net Payable</p>
            <p className="text-lg font-black text-blue-700">₹{(formData.amount || 0) - (isDeductingAdvance ? advanceDeduction : 0)}</p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={isSaving}
          className={cn(
            "px-8 py-2.5 bg-blue-600 text-white rounded font-bold shadow-lg shadow-blue-500/20 active:scale-95 transition-all flex items-center gap-2",
            isSaving ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
          )}
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSaving ? "Saving..." : "Confirm & Save"}
        </button>
      </div>
    </div>
  );
};

const Calculator = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="16" y1="14" x2="16" y2="18" />
    <path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" /><path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M12 18h.01" /><path d="M8 18h.01" />
  </svg>
);

export default SalaryForm;
